import React, { createContext, useState } from "react";

// ── slugify — exact copy of MainPage.js to ensure slug-matching is identical ──
export function slugifyForClient(str) {
  return str
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * makeQuestionFilter(questionMode) → (key, group) => boolean
 *
 * questionMode === "all"  → general/explore filter: client:"all", not turnout, not hidden
 * questionMode === <slug> → client filter: slug-matches company, not hidden
 *
 * Mirrors filterByClient in MainPage.js exactly (array-aware, slug-compare).
 */
export function makeQuestionFilter(questionMode) {
  if (!questionMode || questionMode === "all") {
    return (key, group) => {
      const clients = Array.isArray(group.client) ? group.client : [group.client];
      return clients.includes("all") && key !== "turnout" && !group.hidden;
    };
  }
  return (key, group) => {
    const clients = Array.isArray(group.client) ? group.client : [group.client];
    return clients.some(c => slugifyForClient(c) === slugifyForClient(questionMode)) && !group.hidden;
  };
}

// ── Context ───────────────────────────────────────────────────────────────────

const SelectionContext = createContext({
  selectedVariable: null,
  setSelectedVariable: () => {},
  waveSelect: null,
  setWaveSelect: () => {},
  preferenceFilter: null,
  setPreferenceFilter: () => {},
  selectedConstituencyCode: null,
  setSelectedConstituencyCode: () => {},
  selectedConstituencyName: null,
  setSelectedConstituencyName: () => {},
  geoLevel: "constituency",
  setGeoLevel: () => {},
  // Question-set parameterisation:
  //   questionMode   — "all" for the general section; company_slug for the client section
  //   sectionDefaultVariable — first valid question for the current mode (used as fallback)
  questionMode: "all",
  setQuestionMode: () => {},
  sectionDefaultVariable: "vote_intention",
  setSectionDefaultVariable: () => {},
});

export function SelectionProvider({ children }) {
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [waveSelect, setWaveSelect] = useState(null);
  const [preferenceFilter, setPreferenceFilter] = useState(null);
  const [selectedConstituencyCode, setSelectedConstituencyCode] = useState(null);
  const [selectedConstituencyName, setSelectedConstituencyName] = useState(null);
  const [geoLevel, setGeoLevel] = useState("constituency");
  const [questionMode, setQuestionMode] = useState("all");
  const [sectionDefaultVariable, setSectionDefaultVariable] = useState("vote_intention");

  return (
    <SelectionContext.Provider
      value={{
        selectedVariable,
        setSelectedVariable,
        waveSelect,
        setWaveSelect,
        preferenceFilter,
        setPreferenceFilter,
        selectedConstituencyCode,
        setSelectedConstituencyCode,
        selectedConstituencyName,
        setSelectedConstituencyName,
        geoLevel,
        setGeoLevel,
        questionMode,
        setQuestionMode,
        sectionDefaultVariable,
        setSectionDefaultVariable,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}

export default SelectionContext;
